import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useGetAvailableConnectorsQuery } from 'src/api/integrations'
import settingsService from 'src/services/settings.service'

const INCLUDE_TYPES = {
  allUsers: 'ALL_USERS',
  allUsersAndGroups: 'ALL_USERS_AND_GROUPS',
  onlySpecificUsers: 'ONLY_SPECIFIC_USERS'
}

export function useOnboardingIntegrationsContext(onSuccessfulFirstSync) {
  const {
    data: rawConnectorsData,
    isLoading: isLoadingConnectors,
    refetch: refetchConnectors
  } = useGetAvailableConnectorsQuery()

  const prevConnectorsDataRef = useRef()

  const [showConfigureDialog, setShowConfigureDialog] = useState(false)
  const [selectedConnector, setSelectedConnector] = useState(null)
  const [open, setOpen] = useState(false)

  const [cooldownConnectors, setCooldownConnectors] = useState({})
  const [activeCooldownIntervals, setActiveCooldownIntervals] = useState({})

  const [adUpdateDialogOpen, setAdUpdateDialogOpen] = useState(false)

  const integrationTitles = {
    GOOGLE: 'Google Workspace',
    OUTLOOK: 'Outlook',
    AD: 'Entra ID (Azure AD)',
    OKTA: 'Okta',
    MICROSOFT: 'Microsoft Entra ID'
  }

  const startCooldown = useCallback(
    connectorType => {
      if (activeCooldownIntervals[connectorType]) {
        clearInterval(activeCooldownIntervals[connectorType])
      }

      setCooldownConnectors(prev => ({
        ...prev,
        [connectorType]: { time: 30, loading: true }
      }))

      const intervalId = setInterval(() => {
        setCooldownConnectors(prev => {
          const currentConnectorState = prev[connectorType]

          if (!currentConnectorState || typeof currentConnectorState.time !== 'number') {
            clearInterval(intervalId)

            setActiveCooldownIntervals(prevIntervals => {
              const newIntervals = { ...prevIntervals }
              delete newIntervals[connectorType]
              return newIntervals
            })

            const newState = { ...prev }
            delete newState[connectorType]
            return newState
          }

          const currentTime = currentConnectorState.time

          if (currentTime <= 1) {
            clearInterval(intervalId)
            setActiveCooldownIntervals(prevIntervals => {
              const newIntervals = { ...prevIntervals }
              delete newIntervals[connectorType]
              return newIntervals
            })

            const finalState = { ...prev }
            delete finalState[connectorType]
            return finalState
          }

          return {
            ...prev,
            [connectorType]: { ...currentConnectorState, time: currentTime - 1 }
          }
        })
      }, 1000)

      setActiveCooldownIntervals(prev => ({ ...prev, [connectorType]: intervalId }))
    },
    [activeCooldownIntervals]
  )

  useEffect(() => {
    return () => {
      Object.values(activeCooldownIntervals).forEach(clearInterval)
    }
  }, [activeCooldownIntervals])

  const query = useMemo(() => new URLSearchParams(), [])

  const connectors = useMemo(() => {
    if (!rawConnectorsData) {
      return []
    }

    if (typeof rawConnectorsData.map !== 'function') {
      return []
    }
    const validSyncTypeValues = Object.values(INCLUDE_TYPES)
    return rawConnectorsData
      .map(connector => {
        if (!connector || typeof connector !== 'object') {
          return null // Or some default valid structure, or filter it out
        }
        const hasValidSyncType = connector.syncType && validSyncTypeValues.includes(connector.syncType)
        return {
          ...connector,
          syncType: hasValidSyncType ? connector.syncType : INCLUDE_TYPES.allUsers
        }
      })
      .filter(Boolean)
  }, [rawConnectorsData])

  const openAdUpdateDialog = useCallback(() => {
    setShowConfigureDialog(false)
    setSelectedConnector('AD')
    setAdUpdateDialogOpen(true)
  }, [])

  const handleConnectTo = useCallback(
    connectorType => {
      if (connectorType === 'GOOGLE') {
        settingsService.getGoogleAuthUrl().then(resp => {
          window.location = resp.data
        })
        return
      }
      if (connectorType === 'OUTLOOK') {
        settingsService.getOutlookAuthUrl().then(resp => {
          window.location = resp.data
        })
        return
      }
      if (connectorType === 'AD') {
        openAdUpdateDialog()

        return
      }
      setSelectedConnector(connectorType)
      setShowConfigureDialog(true)
    },
    [connectors]
  )

  useEffect(() => {
    const currentConnectors = connectors
    const previousConnectors = prevConnectorsDataRef.current

    const currentlySyncedCount = currentConnectors.filter(c => c.isSynced).length
    const previouslySyncedCount = previousConnectors ? previousConnectors.filter(c => c.isSynced).length : 0

    if (
      onSuccessfulFirstSync &&
      typeof onSuccessfulFirstSync === 'function' &&
      previouslySyncedCount === 0 &&
      currentlySyncedCount > 0
    ) {
      try {
        onSuccessfulFirstSync()
      } catch (e) {
        console.error('[DEBUG UIC] Error calling onSuccessfulFirstSync():', e)
      }
    }

    prevConnectorsDataRef.current = currentConnectors
  }, [connectors, isLoadingConnectors, onSuccessfulFirstSync])

  const handleRefetchConnectors = useCallback(async () => {
    try {
      await refetchConnectors().unwrap()
    } catch (error) {
      console.error('[DEBUG UIC] Error from refetchConnectors().unwrap():', error)
    }
  }, [refetchConnectors])

  const disconnect = useCallback(
    async connectorType => {
      if (!connectorType) {
        console.error('[DEBUG UIC] Disconnect called without connectorType')
        return
      }
      try {
        await settingsService.disconnect(connectorType)

        await handleRefetchConnectors()
      } catch (error) {
        console.error(`[DEBUG UIC] Error disconnecting ${connectorType}:`, error)

        throw error
      }
    },
    [handleRefetchConnectors]
  )

  return {
    connectors,
    updateConnectors: handleRefetchConnectors,
    query,
    handleConnectTo,
    showConfigureDialog,
    setShowConfigureDialog,
    selectedConnector,
    setSelectedConnector,
    open,
    setOpen,
    startCooldown,
    cooldownConnectors,
    setCooldownConnectors,
    isLoadingConnectors,
    openAdUpdateDialog,
    adUpdateDialogOpen,
    setAdUpdateDialogOpen,
    integrationTitles,
    disconnect
  }
}
